import type { CodedOrderPayload } from '@/encounter/lib/order-payload';
import {
  formatItemCode,
  payloadToItem,
  stripNotKept,
  withSourceSystem,
  type ItemFromPayload,
} from '@/settings/components/order-sets/order-set-item-details';
import type { CreateOrderSetBody, OrderSetDiagnosis, OrderSetItemInput } from '@/types/order-set-api';

/**
 * "Save as order set" from the A/P (PROD-2722, v3 boards 7 and 8). Pure: the dialog owns the
 * ticks and the name, these functions own what goes on the wire.
 *
 * Each A/P order becomes an order set item through the Settings builder's OWN mapping
 * (`payloadToItem`, PROD-2714), so a set saved here opens in Settings › Order sets exactly like
 * one built there, and the PROD-2828 rule keeps the coding system the A/P order carried
 * (`withSourceSystem`). Nothing of this visit is kept (board 7: "Not kept: the patient, dates,
 * notes to the lab, order and lot numbers"): `payloadToItem` builds the entry with no patient,
 * encounter or performer and strips every not-kept key, and `toSaveAsBody` strips again before
 * the POST. The Patient service's BundleDetailsSanitizer strips the same keys a third time.
 *
 * Grouping follows the board: one block per diagnosis of the visit, its orders indented under
 * it. An order linked to several diagnoses sits under its first (the one the A/P save nests it
 * under). Referrals have no set form (the builder offers none) and education articles are not
 * on the board's list, so neither is offered.
 */

/** The canvas diagnosis, as much of it as the list reads (the DS `DxCard` and the mapper's agree). */
export interface SaveAsDx {
  id: string;
  code?: string;
  display: string;
  isPrimary?: boolean;
}

/** The canvas order, as much of it as the list reads. */
export interface SaveAsOrder {
  id: string;
  label: string;
  dxIds: string[];
}

/** The A/P's diagnosis coding side-map (keeps the code the canvas card may not). */
export type SaveAsDxCoding = ReadonlyMap<string, { code?: string; display?: string }>;

/** One order the physician may tick. */
export interface SaveAsRow {
  /** Tick key, `o:<PlanOrder.id>`. */
  key: string;
  label: string;
  /** The board's grey trailing text: "LOINC 2160-0", "CPT 92250", a medication's route ("Oral"). */
  meta: string;
  item: ItemFromPayload;
}

/** One diagnosis block of the list. */
export interface SaveAsGroup {
  /** Tick key, `d:<DxCard.id>`; null for orders with no diagnosis on this visit. */
  key: string | null;
  /** The set diagnosis this block adds; null when the visit's diagnosis has no ICD-10 code. */
  diagnosis: OrderSetDiagnosis | null;
  display: string;
  code: string;
  isPrimary: boolean;
  rows: SaveAsRow[];
}

/** The coding the A/P order carried, for `withSourceSystem`. */
function sourceCoding(
  payload: CodedOrderPayload,
): { code?: string | null; system?: string | null } | null | undefined {
  switch (payload.kind) {
    case 'medication':
      return payload.medicationCode;
    case 'lab':
    case 'imaging':
      return payload.code;
    case 'procedure':
      // The A/P procedure says which coding it is (`system`), even when its code object does not.
      return { code: payload.code.code, system: payload.code.system || (payload.system === 'snomed' ? 'snomed' : 'CPT') };
    case 'vaccination':
      return payload.entry.vaccine ?? null;
    default:
      return null;
  }
}

/** The order's item, through the builder's own mapping. Null for a kind a set cannot hold. */
export function orderToItem(order: SaveAsOrder, payload: CodedOrderPayload | undefined): ItemFromPayload | null {
  if (!payload) return null;
  const base = payloadToItem(payload, order.label);
  if (!base) return null;
  return withSourceSystem(base, sourceCoding(payload));
}

function rowMeta(item: ItemFromPayload, payload: CodedOrderPayload): string {
  if (payload.kind === 'medication') return payload.route?.displayName?.trim() ?? '';
  return formatItemCode(item);
}

function toRow(order: SaveAsOrder, payload: CodedOrderPayload | undefined): SaveAsRow | null {
  const item = orderToItem(order, payload);
  if (!item || !payload) return null;
  return { key: `o:${order.id}`, label: order.label || item.code.display, meta: rowMeta(item, payload), item };
}

/** The dialog's list: the visit's diagnoses in order, each with the orders that sit under it. */
export function buildSaveAsGroups(
  diagnoses: readonly SaveAsDx[],
  orders: readonly SaveAsOrder[],
  payloads: ReadonlyMap<string, CodedOrderPayload>,
  dxCoding?: SaveAsDxCoding,
): SaveAsGroup[] {
  const dxIds = new Set(diagnoses.map((d) => d.id));
  const rowsByDx = new Map<string | null, SaveAsRow[]>();
  for (const order of orders) {
    const row = toRow(order, payloads.get(order.id));
    if (!row) continue;
    const parent = order.dxIds.find((id) => dxIds.has(id)) ?? null;
    rowsByDx.set(parent, [...(rowsByDx.get(parent) ?? []), row]);
  }

  const groups: SaveAsGroup[] = diagnoses.map((dx) => {
    const coding = dxCoding?.get(dx.id);
    const code = (dx.code ?? coding?.code ?? '').trim();
    const display = (dx.display || coding?.display || code).trim();
    return {
      key: `d:${dx.id}`,
      diagnosis: code ? { system: 'ICD-10', code, display } : null,
      display,
      code,
      isPrimary: !!dx.isPrimary,
      rows: rowsByDx.get(dx.id) ?? [],
    };
  });
  const loose = rowsByDx.get(null);
  if (loose?.length) {
    groups.push({ key: null, diagnosis: null, display: '', code: '', isPrimary: false, rows: loose });
  }
  return groups;
}

/**
 * What starts ticked (board 7): the primary diagnosis and its orders. With no primary, the first
 * diagnosis. The physician ticks the rest.
 */
export function initialTicks(groups: SaveAsGroup[]): Set<string> {
  const lead = groups.find((g) => g.key && g.isPrimary) ?? groups.find((g) => g.key);
  const ticks = new Set<string>();
  if (!lead?.key) return ticks;
  ticks.add(lead.key);
  for (const r of lead.rows) ticks.add(r.key);
  return ticks;
}

/** A diagnosis box: on when every order under it is on (or, with none, when it is ticked itself). */
export function groupState(group: SaveAsGroup, ticks: Set<string>): { checked: boolean; indeterminate: boolean } {
  if (group.rows.length === 0) return { checked: !!group.key && ticks.has(group.key), indeterminate: false };
  const on = group.rows.filter((r) => ticks.has(r.key)).length;
  return { checked: on === group.rows.length, indeterminate: on > 0 && on < group.rows.length };
}

/** Ticking a diagnosis ticks it and every order under it; unticking clears them all. */
export function toggleGroup(group: SaveAsGroup, ticks: Set<string>, on: boolean): Set<string> {
  const next = new Set(ticks);
  const keys = [...(group.key ? [group.key] : []), ...group.rows.map((r) => r.key)];
  for (const k of keys) {
    if (on) next.add(k);
    else next.delete(k);
  }
  return next;
}

/** Ticking an order. Its diagnosis follows: on with its first order, off with its last. */
export function toggleRow(group: SaveAsGroup, row: SaveAsRow, ticks: Set<string>, on: boolean): Set<string> {
  const next = new Set(ticks);
  if (on) next.add(row.key);
  else next.delete(row.key);
  if (group.key) {
    if (group.rows.some((r) => next.has(r.key))) next.add(group.key);
    else next.delete(group.key);
  }
  return next;
}

const itemIdentity = (i: ItemFromPayload) =>
  `${i.type}|${(i.code.system ?? '').trim().toLowerCase()}|${(i.code.code ?? '').trim().toLowerCase()}`;

/** The ticked orders, in list order, one per coded order (the same order under two diagnoses once). */
export function tickedItems(groups: SaveAsGroup[], ticks: Set<string>): ItemFromPayload[] {
  const seen = new Set<string>();
  const out: ItemFromPayload[] = [];
  for (const g of groups) {
    for (const r of g.rows) {
      if (!ticks.has(r.key)) continue;
      const id = itemIdentity(r.item);
      if (r.item.code.code && seen.has(id)) continue;
      seen.add(id);
      out.push(r.item);
    }
  }
  return out;
}

/** The set's diagnoses: the coded diagnoses whose box is on, or that hold a ticked order. */
export function tickedDiagnoses(groups: SaveAsGroup[], ticks: Set<string>): OrderSetDiagnosis[] {
  const out: OrderSetDiagnosis[] = [];
  const seen = new Set<string>();
  for (const g of groups) {
    if (!g.diagnosis || !g.key) continue;
    const on = ticks.has(g.key) || g.rows.some((r) => ticks.has(r.key));
    if (!on || seen.has(g.diagnosis.code)) continue;
    seen.add(g.diagnosis.code);
    out.push(g.diagnosis);
  }
  return out;
}

/** The POST body. Every order is a default (board 7: "Kept as defaults"), in list order. */
export function toSaveAsBody(name: string, groups: SaveAsGroup[], ticks: Set<string>): CreateOrderSetBody {
  const items: OrderSetItemInput[] = tickedItems(groups, ticks).map((item, i) => ({
    type: item.type,
    addByDefault: true,
    sortOrder: i + 1,
    code: { system: item.code.system ?? '', code: item.code.code ?? '', display: item.code.display ?? '' },
    details: stripNotKept(item.details ?? {}),
  }));
  return { name: name.trim(), diagnoses: tickedDiagnoses(groups, ticks), items };
}

/** Why Save is off, or null. The API needs a name and at least one order. */
export function saveAsError(name: string, groups: SaveAsGroup[], ticks: Set<string>): string | null {
  if (name.trim().length === 0) return 'Name is required.';
  if (tickedItems(groups, ticks).length === 0) return 'Tick at least one order.';
  return null;
}

/** Board 8's toast. */
export function savedToast(name: string): string {
  return `Saved “${name.trim()}”. Check its defaults in Settings › Order sets.`;
}
